import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FlaskConical, Loader2, RotateCcw, Zap } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  { v: "starter", label: "Essentiel (3 surveillances)" },
  { v: "pro", label: "Pro (15 surveillances + export)" },
  { v: "business", label: "Premium (illimité + enchères)" },
];

export default function TestSubscription({ clients = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [plan, setPlan] = useState("pro");
  const [busy, setBusy] = useState("");

  const activate = async () => {
    if (!clientId) { toast.error("Choisissez un compte utilisateur."); return; }
    setBusy("activate");
    try {
      await api.post("/admin/test-subscription", { client_id: clientId, plan });
      toast.success("Abonnement test activé — accès RADAR débloqué avec les limites de la formule.");
      setOpen(false); onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const reset = async () => {
    if (!clientId) { toast.error("Choisissez un compte à réinitialiser."); return; }
    setBusy("reset");
    try {
      await api.post(`/admin/test-subscription/${clientId}/reset`);
      toast.success("Abonnement réinitialisé — accès RADAR retiré.");
      setOpen(false); onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mb-8 rounded-2xl border border-dashed border-amber-300 bg-amber-50/60 p-6" data-testid="test-sub-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100"><FlaskConical className="h-5 w-5 text-amber-700" /></div>
          <div>
            <h2 className="flex items-center gap-2 font-display text-xl text-[#0F172A]">
              Activer un abonnement test
              <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">Mode test</span>
            </h2>
            <p className="mt-1 max-w-xl text-sm text-slate-600">Débloque immédiatement l'accès RADAR pour un compte, sans paiement, avec les limites de la formule choisie. Réservé aux tests — invisible pour les clients.</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="test-sub-open-btn" className="h-10 rounded-full bg-amber-600 px-5 text-white hover:bg-amber-700">
          <Zap className="mr-1.5 h-4 w-4" /> Activer un abonnement test
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="test-sub-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Abonnement test (sans paiement)</DialogTitle>
            <DialogDescription>Active un abonnement fictif pour tester tout le parcours utilisateur.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Compte utilisateur</label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="mt-1 h-10" data-testid="test-sub-client"><SelectValue placeholder="Choisir un compte" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.email} — {c.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Formule</label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="mt-1 h-10" data-testid="test-sub-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{PLANS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={reset} disabled={busy === "reset"} data-testid="test-sub-reset-btn" className="rounded-full text-red-600 hover:bg-red-50">
              {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="mr-1.5 h-4 w-4" /> Réinitialiser (aucun accès)</>}
            </Button>
            <Button onClick={activate} disabled={busy === "activate"} data-testid="test-sub-activate-btn" className="rounded-full bg-amber-600 text-white hover:bg-amber-700">
              {busy === "activate" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activer l'abonnement test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
